import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { rupees } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { LoadingBlock } from "@/components/EmptyState";
import { useAuth } from "@/context/AuthContext";
import {
    AlertTriangle, Wallet, ShieldCheck, FolderLock, TrendingUp,
    Landmark, Home, Sparkles, ArrowRight, CalendarClock,
} from "lucide-react";

const sevColor = {
    high: "border-destructive/50 bg-destructive/5 text-destructive",
    medium: "border-accent/40 bg-accent/5 text-accent",
    low: "border-border bg-secondary text-muted-foreground",
};

export default function Dashboard() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([api.get("/summary"), api.get("/alerts")])
            .then(([s, a]) => {
                setSummary(s.data);
                setAlerts(a.data);
            })
            .finally(() => setLoading(false));
    }, []);

    const modules = [
        { to: "/vault", label: "Document Vault", icon: FolderLock, count: summary?.counts.documents },
        { to: "/insurance", label: "Insurance", icon: ShieldCheck, count: summary?.counts.insurance },
        { to: "/investments", label: "Investments", icon: TrendingUp, count: summary?.counts.investments },
        { to: "/accounts", label: "Bank & FDs", icon: Landmark, count: summary?.counts.accounts },
        { to: "/assets", label: "Assets", icon: Home, count: summary?.counts.assets },
    ];

    return (
        <div data-testid="dashboard-page">
            <PageHeader
                eyebrow={`Hello, ${user?.name?.split(" ")[0] || "there"}`}
                title="Your life, at a glance."
                subtitle="A calm overview of net worth, alerts, and the modules to keep updated."
                testid="dashboard-header"
            />
            <div className="px-8 py-8 space-y-10">
                {loading ? <LoadingBlock /> : (
                    <>
                        {/* Net worth + summary */}
                        <section className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            <div className="stat-card md:col-span-2">
                                <div className="label-tag">Net worth</div>
                                <div className="mt-2 font-display text-4xl lg:text-5xl font-bold font-mono tracking-tight">
                                    {rupees(summary.net_worth)}
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <div className="text-muted-foreground">Assets</div>
                                        <div className="font-mono">{rupees(summary.total_assets)}</div>
                                    </div>
                                    <div>
                                        <div className="text-muted-foreground">Liabilities</div>
                                        <div className="font-mono">{rupees(summary.liabilities)}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="label-tag">Cash & FDs</div>
                                <div className="mt-2 font-display text-2xl font-bold font-mono">{rupees(summary.cash)}</div>
                                <Wallet className="mt-3 h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                            </div>
                            <div className="stat-card">
                                <div className="label-tag">Life cover</div>
                                <div className="mt-2 font-display text-2xl font-bold font-mono">{rupees(summary.life_cover)}</div>
                                <ShieldCheck className="mt-3 h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                            </div>
                        </section>

                        {/* Modules */}
                        <section>
                            <div className="flex items-end justify-between mb-4">
                                <h2 className="font-display text-xl font-semibold">Modules</h2>
                                <Link to="/health-score" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                                    <Sparkles className="h-4 w-4" /> Get AI health score
                                </Link>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                                {modules.map((m) => (
                                    <Link
                                        key={m.to}
                                        to={m.to}
                                        data-testid={`module-${m.to.slice(1)}`}
                                        className="module-card"
                                    >
                                        <m.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                                        <div className="mt-4 text-sm font-semibold">{m.label}</div>
                                        <div className="mt-1 font-mono text-xs text-muted-foreground">
                                            {m.count ?? 0} entries
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </section>

                        {/* Alerts */}
                        <section>
                            <div className="flex items-end justify-between mb-4">
                                <div>
                                    <h2 className="font-display text-xl font-semibold">Smart alerts</h2>
                                    <p className="text-sm text-muted-foreground">Next 90 days</p>
                                </div>
                                <span className="label-tag">{alerts.length} pending</span>
                            </div>
                            {alerts.length === 0 ? (
                                <div className="rounded-lg border border-dashed border-border/80 p-8 text-center text-sm text-muted-foreground">
                                    All quiet. Nothing due soon.
                                </div>
                            ) : (
                                <ul className="divide-y divide-border rounded-lg border border-border bg-card">
                                    {alerts.slice(0, 8).map((a) => (
                                        <li key={a.id} className="flex items-start gap-4 px-5 py-4">
                                            <div className={`rounded-full border p-2 ${sevColor[a.severity] || sevColor.low}`}>
                                                {a.kind === "doc_expiry" ? <CalendarClock className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-semibold truncate">{a.title}</div>
                                                <div className="text-xs text-muted-foreground">{a.subtitle}</div>
                                            </div>
                                            <div className="text-right text-xs">
                                                {a.due_date ? (
                                                    <>
                                                        <div className="font-mono">{a.due_date}</div>
                                                        <div className="text-muted-foreground">{a.days >= 0 ? `in ${a.days}d` : `overdue ${Math.abs(a.days)}d`}</div>
                                                    </>
                                                ) : (
                                                    <span className="font-semibold text-destructive">Action needed</span>
                                                )}
                                            </div>
                                            <Link to={`/${a.module}`} className="text-xs text-primary inline-flex items-center gap-1 hover:underline">
                                                Open <ArrowRight className="h-3 w-3" />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
}
